import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import {
  buildSuiteLoungeEntryPath,
  useSuiteLoungeFlow,
} from '@/hooks/useSuiteLoungeFlow';
import { useUser } from '@/hooks/useUser';

interface HeaderProps {
  showLanguageMenu?: boolean;
  showNavMenu?: boolean;
}

export const Header = ({
  showLanguageMenu = true,
  showNavMenu = true,
}: HeaderProps) => {
  const { i18n, t } = useTranslation();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { isSuiteLoungeFlow, withEntry } = useSuiteLoungeFlow();
  const { wasSuiteLoungeMember } = useUser();
  const logoPath = isSuiteLoungeFlow
    ? withEntry(isAuthenticated ? ROUTE_PATH.TOP : ROUTE_PATH.LOGIN)
    : ROUTE_PATH.TOP;

  const toggleLanguageMenu = () => {
    setIsLanguageOpen(!isLanguageOpen);
  };

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
    // bodyにis-lockクラスを追加/削除してスクロールを制御
    if (!isNavOpen) {
      document.body.classList.add('is-lock');
    } else {
      document.body.classList.remove('is-lock');
    }
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLanguageOpen(false);
  };

  const handleLogout = () => {
    if (window.confirm(t('header.menu.logoutConfirm'))) {
      logout();
      // SL会員はログアウト後もSL導線(?entry=sl)を維持する
      navigate(
        wasSuiteLoungeMember
          ? buildSuiteLoungeEntryPath(ROUTE_PATH.LOGIN)
          : ROUTE_PATH.LOGIN,
      );
    }
    setIsNavOpen(false);
    document.body.classList.remove('is-lock');
  };

  const handleNavItemClick = (path: string) => {
    setIsNavOpen(false);
    document.body.classList.remove('is-lock');
    navigate(withEntry(path));
  };

  // クリーンアップ：コンポーネントのアンマウント時にbodyのクラスを削除
  useEffect(() => {
    return () => {
      document.body.classList.remove('is-lock');
    };
  }, []);

  return (
    <header className="header" data-js="site-header">
      <div className="header__line">
        <div className="header__inner">
          <div className="header__logo">
            <Link className="header__logo-link" to={logoPath}>
              <img
                className="header__logo-img"
                src="/Common_ANA_Logo.svg"
                alt={t('common.altText.anaLogo')}
              />
            </Link>
          </div>
          <div className="header__menu">
            {/* 言語切替 */}
            {showLanguageMenu && (
              <div
                className={`header__language ${isLanguageOpen ? 'header__language--open' : ''}`}
              >
                <button
                  className="header__language-toggle"
                  type="button"
                  onClick={toggleLanguageMenu}
                >
                  <img
                    className="header__language-icon"
                    src="/Common_Icon_Language.png"
                    alt={t('common.altText.languageIcon')}
                  />
                </button>
                <div className="header__language-list">
                  <button
                    className={`header__language-item ${i18n.language === 'ja' ? 'is-selected' : ''}`}
                    onClick={() => changeLanguage('ja')}
                    type="button"
                  >
                    <span className="header__language-label header__language-label--ja">
                      日本語
                    </span>
                  </button>
                  <button
                    className={`header__language-item ${i18n.language === 'en' ? 'is-selected' : ''}`}
                    onClick={() => changeLanguage('en')}
                    type="button"
                  >
                    <span className="header__language-label header__language-label--en">
                      English
                    </span>
                  </button>
                </div>
              </div>
            )}
            {/* ナビ */}
            {showNavMenu && (
              <nav
                className={`header__nav ${isNavOpen ? 'header__nav--open' : ''}`}
              >
                <button
                  className="header__nav-toggle"
                  type="button"
                  onClick={toggleNav}
                >
                  <span className="u-visually-hidden">メニュー</span>
                  <span className="header__nav-toggle-icon"></span>
                </button>
                <div className="header__nav-list" id="header-nav-list">
                  {isAuthenticated ? (
                    <>
                      <div className="header__nav-item">
                        <button
                          className="header__nav-link"
                          onClick={() => handleNavItemClick(ROUTE_PATH.TOP)}
                          type="button"
                        >
                          {t('header.menu.top')}
                        </button>
                      </div>
                      <div className="header__nav-item">
                        <button
                          className="header__nav-link"
                          onClick={() =>
                            handleNavItemClick(ROUTE_PATH.RESERVATION_HISTORY)
                          }
                          type="button"
                        >
                          {t('header.menu.reservationHistory')}
                        </button>
                      </div>
                      <div className="header__nav-item">
                        <button
                          className="header__nav-link"
                          onClick={() => handleNavItemClick(ROUTE_PATH.MY_PAGE)}
                          type="button"
                        >
                          {t('header.menu.myPage')}
                        </button>
                      </div>
                      <div className="header__nav-item">
                        <button
                          className="header__nav-link"
                          onClick={() => handleNavItemClick(ROUTE_PATH.FAQ)}
                          type="button"
                        >
                          {t('header.menu.faq')}
                        </button>
                      </div>
                      <div className="header__nav-item">
                        <button
                          className="header__nav-link header__nav-link--button"
                          type="button"
                          onClick={handleLogout}
                        >
                          {t('header.menu.logout')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="header__nav-item">
                      <button
                        className="header__nav-link"
                        onClick={() => handleNavItemClick(ROUTE_PATH.FAQ)}
                        type="button"
                      >
                        {t('header.menu.faq')}
                      </button>
                    </div>
                  )}
                </div>
              </nav>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
